import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MessageSquare, Users, Calendar, Shield, Dumbbell, Trophy, ChevronDown,
  MapPin, Briefcase, Coffee, Leaf, User
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTheme } from '@/contexts/ThemeContext';
import { getForumCategories } from '@/lib/supabase/forum';
import { ForumCategory } from '@/types/forum';
import { motion, AnimatePresence } from 'framer-motion';
import { formatRelativeTime } from '@/lib/utils/time';

// Background images for each category
const CATEGORY_BACKGROUNDS: Record<string, string> = {
  'sports-discussion':      'https://images.unsplash.com/photo-1622163642998-1ea32b0bbc67?auto=format&fit=crop&w=1600&q=80',
  'fitness-wellness':       'https://images.unsplash.com/photo-1694861786917-b53dd8a86ea6?auto=format&fit=crop&w=1600&q=80',
  'club-recommendations':   'https://images.unsplash.com/photo-1603722346374-d5e3c673ad3f?auto=format&fit=crop&w=1600&q=80',
  'business-networking':    'https://images.unsplash.com/photo-1573164713988-8665fc963095?auto=format&fit=crop&w=1600&q=80',
  'travel-locations':       'https://images.unsplash.com/photo-1526772662000-3f88f10405ff?auto=format&fit=crop&w=1600&q=80',
  'general-discussion':     'https://images.unsplash.com/photo-1501339847302-ac426a4a7cbb?auto=format&fit=crop&w=1600&q=80',
  'the-rendezvous':         'https://images.unsplash.com/photo-1495837174058-628aafc7d610?auto=format&fit=crop&w=1600&q=80',
};

// Gradient overlays for dark mode
const CATEGORY_GRADIENTS = {
  'sports-discussion':    'from-red-500/30    to-dark-50/95',
  'fitness-wellness':     'from-green-500/30  to-dark-50/95',
  'club-recommendations': 'from-gold-500/30   to-dark-50/95',
  'business-networking':  'from-blue-500/30   to-dark-50/95',
  'travel-locations':     'from-purple-500/30 to-dark-50/95',
  'general-discussion':   'from-teal-500/30   to-dark-50/95',
  'the-rendezvous':       'from-pink-500/30   to-dark-50/95',
};

// Lighter gradients for light mode
const LIGHT_CATEGORY_GRADIENTS = {
  'sports-discussion':    'from-red-500/40    to-light-100/70',
  'fitness-wellness':     'from-green-500/40  to-light-100/70',
  'club-recommendations': 'from-gold-500/40   to-light-100/70',
  'business-networking':  'from-blue-500/40   to-light-100/70',
  'travel-locations':     'from-purple-500/40 to-light-100/70',
  'general-discussion':   'from-teal-500/40   to-light-100/70',
  'the-rendezvous':       'from-pink-500/40   to-light-100/70',
};

const ForumCategoriesList: React.FC = () => {
  const navigate = useNavigate();
  const [expandedDescriptions, setExpandedDescriptions] = useState<Record<string, boolean>>({});
  const { isDarkMode } = useTheme();

  const [categories, setCategories] = useState<ForumCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Description character limit before showing "Read more"
  const DESCRIPTION_LIMIT = 120;

  const toggleDescription = (id: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent navigation when clicking the toggle
    setExpandedDescriptions(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const shouldTruncate = (description: string) => {
    return description && description.length > DESCRIPTION_LIMIT;
  };

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const data = await getForumCategories();
        setCategories(data);
      } catch (err) {
        console.error('Error fetching categories:', err);
        setError('Failed to load categories');
      } finally {
        setIsLoading(false);
      }
    };
    fetchCategories();
  }, []);

  /* --------------------------------------------
   * Helpers
   * ------------------------------------------ */
  const iconProps = { className: 'h-5 w-5 text-gold-400' };
  const IconMap: Record<string, React.ComponentType<any>> = {
    MessageSquare, Users, Calendar, Shield, Dumbbell, Trophy,
    MapPin, Briefcase, Coffee, Leaf
  };

  const getIconComponent = (name: string) => {
    const Icon = IconMap[name] || MessageSquare;
    return <Icon {...iconProps} />;
  };

  /* --------------------------------------------
   * Skeleton / error / empty states
   * ------------------------------------------ */
  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3, 4, 5, 6].map(key => (
          <div
            key={key}
            className={cn(
              'p-6 rounded-xl border animate-pulse',
              isDarkMode ? 'bg-dark-200 border-dark-300'
                         : 'bg-light-100 border-light-300'
            )}
          >
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-dark-300" />
              <div className="flex-1 space-y-2">
                <div className="h-5 bg-dark-300 rounded w-1/3" />
                <div className="h-4 bg-dark-300 rounded w-2/3" />
              </div>
              <div className="space-y-2">
                <div className="h-4 bg-dark-300 rounded w-20" />
                <div className="h-4 bg-dark-300 rounded w-16" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={cn(
          'p-6 rounded-xl border text-center',
          isDarkMode ? 'bg-dark-200 border-dark-300'
                     : 'bg-light-100 border-light-300'
        )}
      >
        <p className="text-red-500 mb-4">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="text-gold-400 hover:text-gold-500 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (categories.length === 0) {
    return (
      <div
        className={cn(
          'p-6 rounded-xl border text-center',
          isDarkMode ? 'bg-dark-200 border-dark-300'
                     : 'bg-light-100 border-light-300'
        )}
      >
        <MessageSquare className="h-12 w-12 mx-auto text-dark-800 mb-4" />
        <h3 className="text-lg font-medium text-white mb-2">No Categories Found</h3>
        <p className="text-dark-800">
          There are no forum categories available at the moment.
        </p>
      </div>
    );
  }

  /* --------------------------------------------
   * Happy-path rendering
   * ------------------------------------------ */
  return (
    <div className="space-y-4">
      {categories.map(category => (
        <div
          key={category.id}
          onClick={() => navigate(`/dashboard/forums/category/${category.slug}`)}
          className={cn(
            'relative p-6 rounded-xl border cursor-pointer transition-all',
            'hover:border-gold-400 hover:shadow-md overflow-hidden h-full',
            isDarkMode ? 'border-dark-300' : 'border-light-300'
          )}
        >
          {/* Background photo */}
          <div
            className={cn(
              'absolute inset-0 z-0',
              isDarkMode ? 'opacity-50' : 'opacity-70'
            )}
            style={{
              backgroundImage: `url(${CATEGORY_BACKGROUNDS[category.slug] || ''})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          />

          {/* Gradient overlay */}
          <div
            className={cn(
              'absolute inset-0 z-0',
              isDarkMode
                ? `bg-gradient-to-b ${CATEGORY_GRADIENTS[category.slug] || 'from-dark-300/50 to-dark-50/90'}`
                : `bg-gradient-to-b ${LIGHT_CATEGORY_GRADIENTS[category.slug] || 'from-light-200/60 to-light-100/80'}`
            )}
          />

          {/* NEW: subtle dark veil only in light mode */}
          {!isDarkMode && (
            <div className="absolute inset-0 z-[1] rounded-xl bg-black/25 pointer-events-none" />
          )}

          {/* Card content */}
          <div className="flex flex-col gap-3 relative z-10 h-full">
            {/* Icon + title */}
            <div className="flex items-center gap-3">
              <div
                className={cn(
                  'w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0',
                  'backdrop-blur-sm border shadow-sm',
                  isDarkMode
                    ? 'bg-dark-300/90 border-dark-400/50'
                    : 'bg-light-200/90 border-light-300/50'
                )}
              >
                {getIconComponent(category.icon)}
              </div>

              <div className="flex-1 min-w-0">
                {/* Title with gold gradient */}
                <h3
                  className={cn(
                    'text-lg md:text-xl lg:text-2xl font-semibold tracking-tight',
                    'bg-gradient-to-r from-white via-amber-300 to-amber-500',
                    'bg-clip-text text-transparent drop-shadow-lg'
                  )}
                >
                  {category.name}
                </h3>

                {/* Description with collapsible functionality */}
                {category.description && (
                  <div className="mt-1">
                    <p 
                      className={cn(
                      "text-white/90 text-sm font-medium drop-shadow-sm",
                      !expandedDescriptions[category.id] && shouldTruncate(category.description) && "line-clamp-2"
                      )}
                      aria-label="Category description"
                    >
                      {expandedDescriptions[category.id] || !shouldTruncate(category.description)
                        ? category.description
                        : `${category.description.substring(0, DESCRIPTION_LIMIT)}...`}
                    </p>
                    
                    {shouldTruncate(category.description) && (
                      <button
                        onClick={(e) => toggleDescription(category.id, e)}
                        className="flex items-center mt-1 text-xs text-gold-400 hover:text-gold-500 transition-colors"
                       aria-expanded={expandedDescriptions[category.id] ? "true" : "false"}
                      >
                        <span>{expandedDescriptions[category.id] ? 'Show less' : 'Read more'}</span>
                        <ChevronDown 
                          className={cn(
                            "h-3 w-3 ml-1 transition-transform duration-200",
                            expandedDescriptions[category.id] && "transform rotate-180"
                          )} 
                        />
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Footer chips */}
            <div
              className={cn(
                'flex items-center gap-4 text-xs mt-3 ml-1 flex-wrap',
                isDarkMode ? 'text-white/80' : 'text-light-800 font-semibold'
              )}
            >
              {[
                {
                  label: `${category.topicsCount} ${category.topicsCount === 1 ? 'topic' : 'topics'}`
                },
                {
                  label: `${category.postsCount} ${category.postsCount === 1 ? 'reply' : 'replies'}`
                },
                category.lastActivity && {
                  label: `Last activity: ${formatRelativeTime(category.lastActivity)}`
                }
              ]
                .filter(Boolean)
                .map(({ label }, idx) => (
                  <div
                    key={idx}
                    className={cn(
                      'px-2 py-1 rounded-full backdrop-blur-sm font-medium shadow-sm',
                      isDarkMode
                        ? 'bg-dark-50/60 text-white'
                        : 'bg-light-300/90 text-light-900'
                    )}
                  >
                    {label}
                  </div>
                ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ForumCategoriesList;